import { createAction, createReducer, createSelector } from "@reduxjs/toolkit";
import { removeById, reorderElement, replaceById } from "../utils/immer.js";
import { presetStateSelector } from "../utils/state.js";
import { findById } from "../utils/dash.js";
import { set } from "es-toolkit/compat";
import { setExtraSpec } from "../utils/actions.js";
import { pushToLayers, filterLayers } from "../utils/layer.js";
import { createLayerTree, getLayerGroups, addLayerGroup, moveLayerItem, removeLayerItem, getVisibleLayers, changeLayerItemVisibility, filterLayerTree, addLayer, } from "@mapka/style-common";
export const selectLayer = createAction("layers/select");
export const deleteLayer = createAction("layers/deleteLayer");
export const hideLayer = createAction("layers/hideLayer");
export const changeSearch = createAction("layers/changeSearch");
export const showLayer = createAction("layers/showLayer");
export const reorderLayer = createAction("layers/reorder");
export const changeLayerGroup = createAction("layers/changeGroup");
export const addHighlightLayers = createAction("layers/addHighlightLayers");
export const removeHighlightLayers = createAction("layers/removeHighlightLayer");
export const setLayersDisplay = createAction("layers/setDisplay");
export const setLayersConfigDisplay = createAction("layers/setConfigDisplay");
export const createLayerGroup = createAction("layers/createLayerGroup");
export const changeLayerConfig = createAction("layers/changeConfig");
export const changeLayerPaintConfig = createAction("layers/changePaintConfig");
const initialState = {
    selected: null,
    listDisplay: "grouped",
    configDisplay: "code",
    search: "",
    groups: [],
    layers: [],
    highlightLayers: [],
};
export const layersReducer = createReducer(initialState, (builder) => {
    builder
        .addCase(selectLayer, (state, { payload }) => {
        state.selected = payload;
    })
        .addCase(setLayersDisplay, (state, { payload }) => {
        state.listDisplay = payload;
    })
        .addCase(setLayersConfigDisplay, (state, { payload }) => {
        state.configDisplay = payload;
    })
        .addCase(changeSearch, (state, { payload }) => {
        state.search = payload;
    })
        .addCase(changeLayerConfig, (state, { payload }) => {
        const { layers, } = state;
        const { id, config, } = payload;
        replaceById(layers, id, config);
    })
        .addCase(changeLayerPaintConfig, (state, { payload }) => {
        const { layers, } = state;
        const { layerId, paint, } = payload;
        const currentLayer = findById(layers, layerId);
        if (!currentLayer) {
            return;
        }
        set(currentLayer, "paint", paint);
        replaceById(layers, layerId, currentLayer);
    })
        .addCase(reorderLayer, (state, { payload }) => {
        const { layers, } = state;
        const { from, to, } = payload;
        reorderElement(layers, from, to);
    })
        .addCase(createLayerGroup, (state, { payload }) => {
        const { groups, } = state;
        addLayerGroup(groups, payload);
    })
        .addCase(deleteLayer, (state, { payload }) => {
        const { layers, groups, } = state;
        removeLayerItem(groups, payload);
        removeById(layers, payload);
    })
        .addCase(hideLayer, (state, { payload }) => {
        const { layers, groups, } = state;
        changeLayerItemVisibility(groups, layers, payload, "none");
    })
        .addCase(showLayer, (state, { payload }) => {
        const { layers, groups, } = state;
        changeLayerItemVisibility(groups, layers, payload, "visible");
    })
        .addCase(setExtraSpec, (state, { payload }) => {
        const { layers: currentLayers, groups, } = state;
        const { layers: layersFromExtraSpec = [], } = payload;
        for (const layer of layersFromExtraSpec) {
            pushToLayers(currentLayers, layer);
            addLayer(groups, {
                value: layer.id,
            });
        }
    })
        .addCase(changeLayerGroup, (state, { payload }) => {
        const { groups, } = state;
        const { from, to, id, } = payload;
        moveLayerItem(groups, id, from, to);
    })
        .addCase(addHighlightLayers, (state, { payload }) => {
        state.highlightLayers = payload;
    })
        .addCase(removeHighlightLayers, (state) => {
        state.highlightLayers = [];
    });
});
export const getLayersState = createSelector([presetStateSelector], present => present.layers);
export const getLayers = createSelector([getLayersState], layers => layers.layers);
export const getHighlightLayers = createSelector([getLayersState], layers => layers.highlightLayers);
export const getAllLayers = createSelector([
    getLayers,
    getHighlightLayers,
], (layers, highlightLayers) => {
    return [...layers, ...highlightLayers];
});
export const getSelected = createSelector([getLayersState], layers => layers.selected);
export const getSelectedLayerId = createSelector([getLayersState], state => state.selected);
export const getLayersListDisplay = createSelector([getLayersState], state => state.listDisplay);
export const getLayersConfigDisplay = createSelector([getLayersState], state => state.configDisplay);
export const getLayersTreeConfig = createSelector([getLayersState], state => state.groups);
export const getLayerSearch = createSelector([getLayersState], state => state.search);
export const getFilteredLayers = createSelector([
    getLayers,
    getLayerSearch,
], filterLayers);
export const getVisibleLayersIds = createSelector([getLayers], getVisibleLayers);
export const getLayerGroupValues = createSelector([getLayersTreeConfig], getLayerGroups);
export const getSelectedLayer = createSelector([
    getLayers,
    getSelected,
], findById);
export const getLayersTree = createSelector([
    getLayersTreeConfig,
    getLayers,
], createLayerTree);
export const getFilteredLayersTree = createSelector([
    getLayersTree,
    getLayerSearch,
], filterLayerTree);
